import React, { FC } from 'react';
import { useRouter } from 'next/router';
import type { Breadcrumb } from 'src/common/types/api/v4/boats/search';
import appendTrailingSlash from 'src/common/utils/appendTrailingSlash';
import formatLocationName from 'src/search/utils/formatLocationName';
import { PATHS } from 'src/common/constants';
import { DEFAULT_SLUG } from 'src/search/constants';
import UniversalLink from 'src/common/components/UniversalLink';
import Typography, { type TypographyProps } from 'src/common/components/Typography';
import Icon, { type IconProps } from 'src/common/components/IconDS22';
import styles from './DlpBreadcrumbs.module.scss';

export type DlpBreadcrumbsProps = {
  breadcrumbs: Breadcrumb[];
  /** Any string (or supported iconId). Will preserve space chars */
  separator?: string | 'caret-right'; // currently only offers one iconId
  typographySize?: TypographyProps['size'];
  typographyWeight?: TypographyProps['weight'];
  iconSize?: IconProps['size'];
};

const DlpBreadcrumbs: FC<DlpBreadcrumbsProps> = ({
  breadcrumbs,
  separator = 'caret-right',
  typographySize = 'body-l',
  typographyWeight = 'regular',
  iconSize = 'xs',
}) => {
  const { asPath } = useRouter();
  return (
    <ul className={styles.root}>
      {breadcrumbs.map(({ indexed, name, slug }, index) => {
        const isLastBreadcrumb = index === breadcrumbs.length - 1;
        const path = appendTrailingSlash(`${PATHS.SEARCH}${slug === DEFAULT_SLUG ? '' : slug}`);
        const isLink = path !== asPath && indexed;
        const breadcrumbName = formatLocationName(name);
        return (
          <li key={slug} className={styles.breadcrumbItem}>
            {isLink ? (
              <UniversalLink
                href={path}
              >
                <Typography type="p" size={typographySize} weight={typographyWeight}>
                  {breadcrumbName}
                </Typography>
              </UniversalLink>
            ) : (
              <Typography type="p" size={typographySize} weight={typographyWeight}>
                {breadcrumbName}
              </Typography>
            )}
            {!isLastBreadcrumb && (
              separator === 'caret-right' ? (
                <div className={styles.iconSeparator}>
                  <Icon id={separator} size={iconSize} />
                </div>
              ) : (
                <Typography type="p" size={typographySize} weight={typographyWeight}>
                  {separator}
                </Typography>
              )
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default DlpBreadcrumbs;
